import React, { useEffect, useMemo, useState } from 'react';
import DocumentDataTable from './DocumentDataTable';
import { useFormBuilder } from '../utils/useFormBuilder';
import { deleteUploadedFile } from '../api/uploadAPI';

const initialFormState = {
  rotableStatusName: '',
  repairStatusName: '',
  createdAt: '',
  createdByName: '',
  assetNumber: '',
  taskLabel: '',
  usage: '',
  notes: '',
  images: [],
  files: [],
};

const buildTaskLabel = (task) => {
  const code = task?.code?.code;
  const name = task?.code?.name;
  return [code, name].filter(Boolean).join(' - ');
};

const RotableTransactionEditModal = ({ show, transaction, createdByUser, onClose, onSave }) => {
  const [formState, setFormState] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    if (!show) return;
    setFormState({
      rotableStatusName: transaction?.rotableStatus?.name ?? '—',
      repairStatusName: transaction?.repairStatus?.name ?? '—',
      createdAt: transaction?.createdAt ?? '',
      createdByName: createdByUser?.name ?? '—',
      assetNumber: transaction?.asset?.assetNumber ?? '—',
      taskLabel: buildTaskLabel(transaction?.task) || '—',
      usage: transaction?.usage ?? '—',
      notes: transaction?.notes ?? '',
      images: Array.isArray(transaction?.images) ? transaction.images : [],
      files: Array.isArray(transaction?.files) ? transaction.files : [],
    });
  }, [show, transaction]);

  useEffect(() => {
    if (!show) return;
    if (!createdByUser?.name) return;
    setFormState((prev) => ({
      ...prev,
      createdByName: createdByUser?.name ?? '—',
    }));
  }, [createdByUser?.name, show]);

  const formFields = useMemo(
    () => [
      {
        field: 'rotableStatusName',
        label: 'Rotable Status',
        type: 'text',
        size: '4',
        disabled: true,
      },
      {
        field: 'repairStatusName',
        label: 'Repair Status',
        type: 'text',
        size: '4',
        disabled: true,
      },
      {
        field: 'createdAt',
        label: 'Created At',
        type: 'datetime',
        size: '4',
        disabled: true,
      },
      {
        field: 'createdByName',
        label: 'Created By',
        type: 'text',
        size: '4',
        disabled: true,
      },
      {
        field: 'assetNumber',
        label: 'Asset',
        type: 'text',
        size: '4',
        disabled: true,
      },
      {
        field: 'taskLabel',
        label: 'Task',
        type: 'text',
        size: '4',
        disabled: true,
      },
      {
        field: 'usage',
        label: 'Usage',
        type: 'text',
        size: '4',
        disabled: true,
      },
      {
        field: 'notes',
        label: 'Notes',
        type: 'textarea',
        size: '12',
        lines: 3,
      },
      {
        field: 'images',
        label: 'Images',
        type: 'images',
        size: '12',
        directory: 'partsfiles',
        onImageClick: (src) => {
          setImageModalSrc(src);
          setShowImageModal(true);
        },
      },
      {
        field: 'files',
        label: 'Files',
        type: 'files',
        size: '12',
        directory: 'partsfiles',
        accept:
          '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg',
      },
    ],
    [],
  );

  const { renderField } = useFormBuilder(formFields, formState, setFormState, {});

  const handleFileDelete = async (filename, index) => {
    if (!window.confirm('Delete this file?')) return;
    setFormState((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
    try {
      await deleteUploadedFile(filename, 'partsfiles');
    } catch (err) {
      console.error('Error deleting file', err);
    }
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave({
        notes: formState.notes || '',
        images: formState.images || [],
        files: formState.files || [],
      });
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal modal-backdrop show d-block" tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Rotable Transaction</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
          </div>
          <div className="modal-body">
            <div className="row g-3">
              {formFields.map(renderField)}
            </div>
            {Array.isArray(formState.files) && formState.files.length > 0 && (
              <DocumentDataTable
                files={formState.files}
                onDelete={handleFileDelete}
                directory="partsfiles"
              />
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      {showImageModal && (
        <div
          className="modal modal-backdrop show d-block"
          onClick={() => setShowImageModal(false)}
          tabIndex="-1"
        >
          <div className="modal-dialog" style={{ maxWidth: '75%' }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <img src={imageModalSrc} alt="view" style={{ width: '100%' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RotableTransactionEditModal;
